import { useQuery } from '@apollo/client';
import { Box, Flex, Heading, Skeleton, useToast } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  AccountDetailsGQLType,
  GetOrganizationsByIdsQueryGQLType,
  GetOrganizationsByIdsQueryVariablesGQLType,
  TAccountTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { GET_ORGANIZATIONS_BY_IDS } from 'packages/organization/requests/organization.gql';
import {
  useGetLedgerAccountsQuery,
  useGetLedgerQuery,
} from 'packages/statements/requests/ledger.gql.generated';
import { ExpensesFeesTable } from 'packages/statements/ui/ExpensesFeesTable';
import { ExpensesFeesTableHeader } from 'packages/statements/ui/ExpensesFeesTableHeader';
import {
  formatExpensesFeesTransactions,
  getCountersignersConfig,
} from 'packages/statements/ui/expensesFeesUtils';

export const ExpensesFeesTableContainer = ({
  accounts,
  creditFacilityId,
  organizationIds,
}: {
  accounts: AccountDetailsGQLType[];
  creditFacilityId: string;
  organizationIds?: string[]; // pass in orgId if we want to query for specific countersigners instead of all countersigners
}) => {
  const toast = useToast();
  const allAccountTypes = _.uniq(_.map(accounts, 'tAccount'));
  const allCountersignerIds =
    organizationIds ?? _.uniq(_.map(accounts, 'organizationId'));

  // default: load past 30 days
  const defaultStartDate = commonDate
    .currentDateTime()
    .minus({ days: 30 })
    .toJSDate();

  const [selectedAccountTypes, setSelectedAccountTypes] = useState<
    TAccountTypeGQLType[] | []
  >(allAccountTypes);

  const [selectedCountersigners, setSelectedCountersigners] = useState<
    string[] | []
  >(allCountersignerIds);

  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const {
    data: statementsData,
    loading: statementsLoading,
  } = useGetLedgerQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      organizationIds: selectedCountersigners,
      accountTypes: selectedAccountTypes,
      startDate: startDate ? commonDate.dateToEpoch(startDate) : null,
      endDate: endDate ? commonDate.dateToEpoch(endDate) : null,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const { data: countersignersData, loading: countersignersLoading } = useQuery<
    GetOrganizationsByIdsQueryGQLType,
    GetOrganizationsByIdsQueryVariablesGQLType
  >(GET_ORGANIZATIONS_BY_IDS, {
    variables: {
      organizationIds: allCountersignerIds,
    },
  });

  const formattedTransactions = formatExpensesFeesTransactions(
    getCountersignersConfig(countersignersData?.organizationsByIds ?? []),
    statementsData?.ledger,
  );

  const expensesFeesFilterChangeHandler = {
    accountTypes: setSelectedAccountTypes,
    countersigners: setSelectedCountersigners,
  };

  if (
    statementsLoading ||
    !statementsData ||
    countersignersLoading ||
    !countersignersData
  ) {
    return <Skeleton data-testid="loading-ledger" height="600px" />;
  }

  return (
    <Box>
      <ExpensesFeesTableHeader
        countersigners={countersignersData?.organizationsByIds || []}
        ledgerAccountTypes={allAccountTypes}
        selectedAccountTypes={selectedAccountTypes}
        selectedCountersigners={selectedCountersigners}
        onChangeHandler={expensesFeesFilterChangeHandler}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        transactions={formattedTransactions}
        isLoading={false}
      />
      <Box height="30px" />

      <ExpensesFeesTable transactionsByDay={formattedTransactions} />
    </Box>
  );
};

export const ExpensesFeesViewContainer = () => {
  const toast = useToast();
  const { creditFacilityId } = useCreditFacilityContext();

  const {
    data: ledgerAccountsData,
    loading: ledgerAccountsLoading,
  } = useGetLedgerAccountsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      ledgerAccountGroup: 'expensesFees',
    },
  });

  if (ledgerAccountsLoading || !ledgerAccountsData) {
    return <></>;
  }

  return (
    <Box>
      <Flex justify="space-between" mb="24px">
        <Heading as="h2" fontSize="2xl">
          Facility Expenses &amp; Fees
        </Heading>
      </Flex>
      <ExpensesFeesTableContainer
        accounts={ledgerAccountsData.ledgerAccounts}
        creditFacilityId={creditFacilityId}
      />
    </Box>
  );
};
